import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { initialApproval, UpdateCandidateStaus } from '../services/apiService';
import { getCompanyCodeFromToken, getUserFromToken } from '../services/authService';
import { toast } from "react-hot-toast";
import { sendCandidateStatusToBusinessOwner } from '../services/sendEmail';
import { useMsal } from '@azure/msal-react';
import { SendCandidateStatusEmailAsync } from '../services/sendMailSMTP';

const CandidatesModal = ({ isOpen, onClose, candidates, hiringManageremail, hiringRequests }) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isStatusModalOpen, setStatuIsModalOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [localCandidates, setLocalCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { instance } = useMsal(); 
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [generalNotes, setGeneralNotes] = useState('');

  // ── Rejection state ──────────────────────────────────────────
  const [rejectCandidate, setRejectCandidate] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectUpdating, setRejectUpdating] = useState(false);
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    setLocalCandidates(candidates);
  }, [candidates]);

  if (!isOpen) return null;

  const filteredCandidates = localCandidates.filter((candidate) => {
    const matchesName = candidate.full_name?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter ? candidate.status === statusFilter : true;
    return matchesName && matchesStatus;
  });

  const handleMoreDetails = (id) => {
    navigate(`/candidate-details?id=${id}`, { state: { id } });
  };

  const appliedCandidates = filteredCandidates.filter(c => c.status === 'Applied');
  const allSelected = selectedCandidates.length === appliedCandidates.length && appliedCandidates.length > 0;

  const handleResumeView = (url) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('Resume not available');
    }
  };

  const handleStatusClick = async () => {
    if (statusUpdating || !selectedCandidate) return;
    setStatusUpdating(true);

    try {
      const userName = getUserFromToken();
      const updatedCandidate = {
        ...selectedCandidate,
        updated_by: userName,
        oldstatus: selectedCandidate.status,
        status: 'Shortlisted',
      };

      const savedRequest = await UpdateCandidateStaus(updatedCandidate);

      if (!savedRequest.statusCode) {
        toast.error('Failed to update candidate.');
      } else {
        toast.success('Candidate updated successfully!');

        setLocalCandidates((prev) =>
          prev.map((c) =>
            c.candidate_id === updatedCandidate.candidate_id ? updatedCandidate : c
          )
        );
        setStatusUpdating(false);
        setStatuIsModalOpen(false);
        setSelectedCandidate(null);
      }
    } catch (error) {
      console.error('Error updating candidate:', error);
      toast.error('An error occurred while updating the candidate.');
      setStatusUpdating(false);
    }
  };

  // ── Reject handler ───────────────────────────────────────────
  const handleRejectCandidate = async () => {
    if (!rejectCandidate) return;
    setRejectUpdating(true);
    try {
      const userName = getUserFromToken();
      const updatedCandidate = {
        ...rejectCandidate,
        updated_by: userName,
        oldstatus: rejectCandidate.status,
        status: 'Rejected',
        SendapprovalNotes: rejectNotes,
      };

      const savedRequest = await UpdateCandidateStaus(updatedCandidate);

      if (!savedRequest.statusCode) {
        toast.error('Failed to reject candidate.');
      } else {
        toast.success('Candidate rejected successfully!');
        setLocalCandidates((prev) =>
          prev.map((c) =>
            c.candidate_id === rejectCandidate.candidate_id ? updatedCandidate : c
          )
        );
        // Also deselect if this candidate was selected for approval
        setSelectedCandidates((prev) =>
          prev.filter((id) => id !== rejectCandidate.candidate_id)
        );
        setRejectCandidate(null);
        setRejectNotes('');
      }
    } catch (error) {
      console.error('Error rejecting candidate:', error);
      toast.error('An error occurred while rejecting the candidate.');
    } finally {
      setRejectUpdating(false);
    }
  };
  // ─────────────────────────────────────────────────────────────

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(appliedCandidates.map(c => c.candidate_id));
    }
  };

  const toggleCandidateSelection = (id) => {
    setSelectedCandidates((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleInitialApproval = async () => {
    try {
      const userRole = getUserFromToken();

      const selectedDetails = localCandidates
        .filter((c) => selectedCandidates.includes(c.candidate_id))
        .map((c) => ({
          candidate_id: c.candidate_id,
          department_id: c.department_id,
        }));

      const bodyReq = {
        Candidates: selectedDetails,
        UpdatedBy: userRole,
        SendapprovalNotes: generalNotes,
        department_id:hiringRequests.department_id,
        request_id: hiringRequests.request_id,
        companyCode: getCompanyCodeFromToken(),
        NextAction: hiringManageremail
      };

      const result = await initialApproval(bodyReq);

      if (result.statusCode === false) {
        toast.error("Failed to update the candidate.");
      } else {
        setLocalCandidates((prevCandidates) =>
          prevCandidates.map((candidate) =>
            selectedCandidates.includes(candidate.candidate_id)
              ? { ...candidate, status: 'Send For Initial Approval' }
              : candidate
          )
        );

        toast.success("Candidates sent for initial approval successfully!");

        try {
          const businessOwnerEmail = hiringManageremail;
          const formattedHiringRequest = {
            requestId: hiringRequests.request_id,
            position: hiringRequests.position,
            requestType: hiringRequests.request_type,
          };
          const formattedCandidates = filteredCandidates.filter((c) =>
            selectedCandidates.includes(c.candidate_id)
          ).map(c => ({
            full_name: c.full_name,
            email: c.email,
            phone_number: c.phone_number || "",
            last_job_title: c.last_job_title || "Not available"
          }));
          
          const candidateStatusRequest = {
            recipientName: "Business Owner",
            recipientEmail: businessOwnerEmail,
            isApprovalRequest: true,
            isRecruiter: false,
            status: "Initial",
            notes: generalNotes,
            hiringRequest: formattedHiringRequest,
            candidates: formattedCandidates,
          };

          await SendCandidateStatusEmailAsync(candidateStatusRequest);
        } catch (emailError) {
          console.error("Failed to send email to business owner:", emailError);
          toast.warn("Candidates approved but email to business owner failed.");
        }
      }
    } catch (error) {
      console.error("Initial approval error:", error);
      toast.error("Failed to send candidates for initial approval.");
    } finally {
      setSelectedCandidates([]);
      setGeneralNotes('');
    }
  };

  return (
    <div
      className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-50 p-4"
      style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ 
          borderColor: 'rgba(18, 76, 114, 0.1)', 
          borderWidth: '1px' 
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b rounded-t-2xl shrink-0" style={{ 
          borderColor: 'rgba(18, 76, 114, 0.1)',
          background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
        }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
              <svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
                Candidates
              </h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                {filteredCandidates.length} candidates found
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="transition-all rounded-full p-1.5"
            style={{ color: 'rgba(18, 76, 114, 0.6)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
            }}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Filters and Actions */}
          <div className="p-6 border-b" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', backgroundColor: 'rgba(18, 76, 114, 0.02)' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                  Search by name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter candidate name..."
                    className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 pl-10"
                    style={{ 
                      borderColor: 'rgba(18, 76, 114, 0.3)',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                      e.target.style.borderColor = 'rgb(18, 76, 114)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                    }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-4 h-4" style={{ color: 'rgba(18, 76, 114, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                  Status Filter
                </label>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
                    style={{ 
                      borderColor: 'rgba(18, 76, 114, 0.3)',
                      backgroundColor: 'white',
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                      e.target.style.borderColor = 'rgb(18, 76, 114)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Applied">Applied</option>
                    <option value="Shortlisted">Shortlisted</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Interviewed">Interviewed</option>
                    <option value="Send For Initial Approval">Pending Initial Approval</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                  Quick Actions
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={toggleSelectAll}
                    disabled={appliedCandidates.length === 0}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex-1"
                    style={{ 
                      color: appliedCandidates.length === 0 ? 'rgba(18, 76, 114, 0.4)' : 'rgb(18, 76, 114)',
                      backgroundColor: appliedCandidates.length === 0 ? 'rgba(18, 76, 114, 0.05)' : 'rgba(18, 76, 114, 0.1)',
                      border: '1px solid rgba(18, 76, 114, 0.2)',
                      cursor: appliedCandidates.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (appliedCandidates.length > 0) {
                        e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (appliedCandidates.length > 0) {
                        e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                      }
                    }}
                  >
                    {allSelected ? 'Deselect All' : 'Select All Applied'}
                  </button>
                </div>
              </div>
            </div>

            {selectedCandidates.length > 0 && (
              <div className="mt-4 p-3 rounded-lg flex items-center justify-between" style={{ 
                backgroundColor: 'rgba(18, 76, 114, 0.05)',
                border: '1px solid rgba(18, 76, 114, 0.1)'
              }}>
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                    <svg className="w-4 h-4" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                      {selectedCandidates.length} candidate{selectedCandidates.length !== 1 ? 's' : ''} selected
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                      Ready for initial approval submission
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table Container */}
          <div className="flex-1 overflow-auto p-6">
            {filteredCandidates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                  <svg className="w-10 h-10" style={{ color: 'rgba(18, 76, 114, 0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <p className="text-gray-500 font-medium mb-2">No candidates found</p>
                <p className="text-sm text-gray-400">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <div className="overflow-auto border rounded-lg" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', maxHeight: '50vh' }}>
                <table className="min-w-full">
                  <thead style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                    <tr>
                      <th className="px-4 py-3 border-r text-left" style={{ 
                        borderColor: 'rgba(18, 76, 114, 0.1)',
                        color: 'rgb(18, 76, 114)'
                      }}>
                        {appliedCandidates.length > 0 && (
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                              className="w-4 h-4 rounded"
                              style={{ 
                                accentColor: 'rgb(18, 76, 114)',
                                borderColor: 'rgba(18, 76, 114, 0.3)'
                              }}
                            />
                            <span className="text-sm font-medium">Select</span>
                          </div>
                        )}
                      </th>
                      <th className="px-4 py-3 border-r text-sm font-medium" style={{ 
                        borderColor: 'rgba(18, 76, 114, 0.1)',
                        color: 'rgb(18, 76, 114)'
                      }}>Candidate</th>
                      <th className="px-4 py-3 border-r text-sm font-medium" style={{
                        borderColor: 'rgba(18, 76, 114, 0.1)',
                        color: 'rgb(18, 76, 114)'
                      }}>Status</th>
                      <th className="px-4 py-3 text-sm font-medium text-center" style={{ 
                        color: 'rgb(18, 76, 114)'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.map((candidate, index) => {
                      const statusColors = {
                        'Applied': { bg: 'rgba(18, 76, 114, 0.1)', text: 'rgb(18, 76, 114)', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
                        'Shortlisted': { bg: 'rgba(16, 185, 129, 0.1)', text: 'rgb(5, 150, 105)', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
                        'Rejected': { bg: 'rgba(239, 68, 68, 0.1)', text: 'rgb(220, 38, 38)', icon: 'M6 18L18 6M6 6l12 12' },
                        'Interviewed': { bg: 'rgba(245, 158, 11, 0.1)', text: 'rgb(217, 119, 6)', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                        'Send For Initial Approval': { bg: 'rgba(168, 85, 247, 0.1)', text: 'rgb(147, 51, 234)', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' }
                      };
                      const statusStyle = statusColors[candidate.status] || { bg: 'rgba(18, 76, 114, 0.1)', text: 'rgb(18, 76, 114)' };

                      return (
                        <tr
                          key={candidate.candidate_id}
                          className="transition-colors duration-150"
                          style={{ 
                            borderBottom: '1px solid rgba(18, 76, 114, 0.05)',
                            backgroundColor: index % 2 === 0 ? 'white' : 'rgba(18, 76, 114, 0.02)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : 'rgba(18, 76, 114, 0.02)';
                          }}
                        >
                          <td className="px-4 py-3 border-r text-center" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                            {candidate.status === 'Applied' ? (
                              <input
                                type="checkbox"
                                checked={selectedCandidates.includes(candidate.candidate_id)}
                                onChange={() => toggleCandidateSelection(candidate.candidate_id)}
                                className="w-4 h-4 rounded"
                                style={{ 
                                  accentColor: 'rgb(18, 76, 114)',
                                  borderColor: 'rgba(18, 76, 114, 0.3)'
                                }}
                              />
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 border-r" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                            <span className="text-xs text-gray-700">{candidate.full_name}</span>
                          </td>
                          <td className="px-4 py-3 border-r" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                            <div className="flex items-center gap-2">
                              <span 
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: statusStyle.bg,
                                  color: statusStyle.text
                                }}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={statusStyle.icon || "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"} />
                                </svg>
                                {candidate.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-2">
                              {/* View Details button */}
                              <button
                                onClick={() => handleMoreDetails(candidate.candidate_id)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all"
                                style={{ 
                                  color: 'rgb(18, 76, 114)',
                                  backgroundColor: 'rgba(18, 76, 114, 0.1)',
                                  border: '1px solid rgba(18, 76, 114, 0.2)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.borderColor = 'rgb(18, 76, 114)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                                  e.currentTarget.style.color = 'rgb(18, 76, 114)';
                                  e.currentTarget.style.borderColor = 'rgba(18, 76, 114, 0.2)';
                                }}
                              >
                                View Details
                              </button>

                              {/* ── Reject button — Applied candidates only ── */}
                              {candidate.status === 'Applied' && (
                                <button
                                  onClick={() => { setRejectCandidate(candidate); setRejectNotes(''); }}
                                  className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all"
                                  style={{
                                    color: 'rgb(220, 38, 38)',
                                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                    border: '1px solid rgba(239, 68, 68, 0.25)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgb(220, 38, 38)';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.borderColor = 'rgb(220, 38, 38)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                                    e.currentTarget.style.color = 'rgb(220, 38, 38)';
                                    e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.25)';
                                  }}
                                >
                                  Reject
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Initial Approval Section */}
        {selectedCandidates.length > 0 && (
          <div className="border-t p-6" style={{ 
            borderColor: 'rgba(18, 76, 114, 0.1)',
            backgroundColor: 'rgba(18, 76, 114, 0.03)'
          }}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(18, 76, 114)' }}>
                Approval Notes <span className="text-sm font-normal" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>(Optional)</span>
              </label>
              <div className="relative">
                <textarea
                  rows="2"
                  className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-none"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                    e.target.style.borderColor = 'rgb(18, 76, 114)';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                  }}
                  placeholder="Add notes for the business owner about these candidates..."
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                />
                <div className="absolute bottom-2 right-2">
                  <span className="text-xs" style={{ color: 'rgba(18, 76, 114, 0.5)' }}>
                    {generalNotes.length}/500
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                <span className="font-medium">{selectedCandidates.length}</span> candidate{selectedCandidates.length !== 1 ? 's' : ''} selected for initial approval
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedCandidates([])}
                  className="px-3 py-1.5 text-xs bg-white border rounded-lg transition-all font-medium"
                  style={{
                    color: 'rgb(18, 76, 114)',
                    borderColor: 'rgba(18, 76, 114, 0.3)'
                  }}
                >
                  Clear Selection
                </button>
                <button
                  onClick={handleInitialApproval}
                  className="px-3 py-1.5 text-xs text-white font-medium rounded-lg transition-all flex items-center gap-1.5"
                  style={{
                    backgroundColor: 'rgb(18, 76, 114)',
                  }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Submit for Initial Approval
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─────────────────── Status Confirm Modal ─────────────────── */}
      {isStatusModalOpen && selectedCandidate && (
        <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-[60] p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.3)' }}>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto" style={{ 
            borderColor: 'rgba(18, 76, 114, 0.1)', 
            borderWidth: '1px' 
          }}>
            <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ 
              borderColor: 'rgba(18, 76, 114, 0.1)',
              background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
            }}>
              <h2 className="text-lg font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
                Confirm Shortlist
              </h2>
              <button
                onClick={() => setStatuIsModalOpen(false)}
                className="transition-all rounded-full p-1.5"
                style={{ color: 'rgba(18, 76, 114, 0.6)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
                }}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Are you sure you want to change the candidate's status to <strong>Shortlisted</strong>?
              </p>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={() => setStatuIsModalOpen(false)}
                  className="px-3 py-1.5 text-xs bg-white border rounded-lg transition-all font-medium"
                  style={{ 
                    color: 'rgb(18, 76, 114)',
                    borderColor: 'rgba(18, 76, 114, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusClick}
                  className="px-3 py-1.5 text-xs text-white font-medium rounded-lg transition-all"
                  style={{ 
                    backgroundColor: statusUpdating ? 'rgba(18, 76, 114, 0.5)' : 'rgb(18, 76, 114)',
                    cursor: statusUpdating ? 'not-allowed' : 'pointer',
                    boxShadow: statusUpdating ? 'none' : '0 10px 15px -3px rgba(18, 76, 114, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!statusUpdating) e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.9)';
                  }}
                  onMouseLeave={(e) => {
                    if (!statusUpdating) e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
                  }}
                  disabled={statusUpdating}
                >
                  {statusUpdating ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Yes, Shortlist'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────── Reject Candidate Modal ─────────────────── */}
      {rejectCandidate && (
        <div
          className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-[60] p-4"
          style={{ backgroundColor: 'rgba(18, 76, 114, 0.3)' }}
          onClick={() => { setRejectCandidate(null); setRejectNotes(''); }}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ borderColor: 'rgba(239, 68, 68, 0.2)', borderWidth: '1px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-6 border-b rounded-t-2xl"
              style={{
                borderColor: 'rgba(239, 68, 68, 0.15)',
                background: 'linear-gradient(to right, rgba(239, 68, 68, 0.05), transparent)'
              }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <svg className="w-5 h-5" style={{ color: 'rgb(220, 38, 38)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
                    Reject Candidate
                  </h2>
                  <p className="text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
                    {rejectCandidate.full_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setRejectCandidate(null); setRejectNotes(''); }}
                className="transition-all rounded-full p-1.5"
                style={{ color: 'rgba(18, 76, 114, 0.6)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
                }}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Warning notice */}
              <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                <svg className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'rgb(220, 38, 38)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm" style={{ color: 'rgb(185, 28, 28)' }}>
                  This will mark <strong>{rejectCandidate.full_name}</strong> as <strong>Rejected</strong>. This action can be reviewed later by an administrator.
                </p>
              </div>

              {/* Rejection notes */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(18, 76, 114)' }}>
                  Rejection Reason <span className="text-sm font-normal" style={{ color: 'rgba(18, 76, 114, 0.5)' }}>(Optional)</span>
                </label>
                <div className="relative">
                  <textarea
                    rows="3"
                    className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-none"
                    style={{
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = '0 0 0 2px rgba(239, 68, 68, 0.15)';
                      e.target.style.borderColor = 'rgb(220, 38, 38)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                    }}
                    placeholder="Provide a reason for rejection..."
                    value={rejectNotes}
                    onChange={(e) => setRejectNotes(e.target.value)}
                  />
                  <div className="absolute bottom-2 right-2">
                    <span className="text-xs" style={{ color: 'rgba(18, 76, 114, 0.4)' }}>
                      {rejectNotes.length}/500
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-1">
                <button
                  onClick={() => { setRejectCandidate(null); setRejectNotes(''); }}
                  className="px-3 py-1.5 text-xs bg-white border rounded-lg transition-all font-medium"
                  style={{
                    color: 'rgb(18, 76, 114)',
                    borderColor: 'rgba(18, 76, 114, 0.3)'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectCandidate}
                  disabled={rejectUpdating}
                  className="px-3 py-1.5 text-xs text-white font-medium rounded-lg transition-all flex items-center justify-center gap-1.5"
                  style={{
                    backgroundColor: rejectUpdating ? 'rgba(220, 38, 38, 0.5)' : 'rgb(220, 38, 38)',
                    cursor: rejectUpdating ? 'not-allowed' : 'pointer',
                    boxShadow: rejectUpdating ? 'none' : '0 10px 15px -3px rgba(220, 38, 38, 0.25)'
                  }}
                  onMouseEnter={(e) => { if (!rejectUpdating) e.currentTarget.style.backgroundColor = 'rgb(185, 28, 28)'; }}
                  onMouseLeave={(e) => { if (!rejectUpdating) e.currentTarget.style.backgroundColor = 'rgb(220, 38, 38)'; }}
                >
                  {rejectUpdating ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Confirm Rejection
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ──────────────────────────────────────────────────────────── */}

    </div>
  );
};

export default CandidatesModal;